import React, { useContext } from 'react';
import { Box, Typography, Stack, CircularProgress } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PaymentIcon from '@mui/icons-material/Payment';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { RestockHistory, RestockHistoryAction } from '../../types';
import { palette, typography } from '../../theme/colors';
import { ColorModeContext } from '../../contexts/ColorModeContext';

interface RestockHistoryTimelineProps {
  history: RestockHistory[] | undefined;
  isLoading?: boolean;
}

interface ActionConfig {
  label: string;
  icon: React.ReactElement;
  color: string;
}

const getActionConfig = (action: RestockHistoryAction): ActionConfig => {
  switch (action) {
    case RestockHistoryAction.RESTOCK_CREATED:
      return {
        label: 'Commande Créée',
        icon: <AddCircleOutlineIcon sx={{ fontSize: 16 }} />,
        color: palette.primary,
      };
    case RestockHistoryAction.RESTOCK_UPDATED:
      return {
        label: 'Commande Mise à Jour',
        icon: <EditIcon sx={{ fontSize: 16 }} />,
        color: palette.info,
      };
    case RestockHistoryAction.ITEMS_ADDED:
      return {
        label: 'Articles Ajoutés',
        icon: <AddShoppingCartIcon sx={{ fontSize: 16 }} />,
        color: palette.success,
      };
    case RestockHistoryAction.ITEMS_REMOVED:
      return {
        label: 'Articles Supprimés',
        icon: <RemoveShoppingCartIcon sx={{ fontSize: 16 }} />,
        color: palette.warning,
      };
    case RestockHistoryAction.STATUS_CONFIRMED:
      return {
        label: 'Commande Confirmée',
        icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
        color: palette.info,
      };
    case RestockHistoryAction.STATUS_RECEIVED:
      return {
        label: 'Commande Reçue',
        icon: <LocalShippingIcon sx={{ fontSize: 16 }} />,
        color: palette.success,
      };
    case RestockHistoryAction.STATUS_PAID:
      return {
        label: 'Paiement Effectué',
        icon: <PaymentIcon sx={{ fontSize: 16 }} />,
        color: palette.accent,
      };
    case RestockHistoryAction.STATUS_CANCELLED:
      return {
        label: 'Commande Annulée',
        icon: <CancelIcon sx={{ fontSize: 16 }} />,
        color: palette.error,
      };
    case RestockHistoryAction.RESTOCK_DUPLICATED:
      return {
        label: 'Commande Dupliquée',
        icon: <ContentCopyIcon sx={{ fontSize: 16 }} />,
        color: palette.info,
      };
    default:
      return {
        label: action,
        icon: <HistoryIcon sx={{ fontSize: 16 }} />,
        color: palette.textMuted,
      };
  }
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString('fr-DZ', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const RestockHistoryTimeline: React.FC<RestockHistoryTimelineProps> = ({
  history,
  isLoading = false,
}) => {
  const { mode } = useContext(ColorModeContext);

  // Theme-aware colors
  const colors = mode === 'dark' ? {
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
    border: palette.border,
  } : {
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
    border: palette.light.border,
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!history || history.length === 0) {
    return (
      <Box py={4} textAlign="center">
        <HistoryIcon sx={{ fontSize: 40, color: colors.textMuted, mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Aucun historique disponible
        </Typography>
      </Box>
    );
  }

  // Sort history by date, most recent first
  const sortedHistory = [...history].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <Stack spacing={0}>
      {sortedHistory.map((entry, index) => {
        const actionConfig = getActionConfig(entry.action);
        const isLast = index === sortedHistory.length - 1;

        return (
          <Box key={entry.id} display="flex" gap={2}>
            {/* Timeline line and dot */}
            <Box display="flex" flexDirection="column" alignItems="center">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: `${actionConfig.color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: actionConfig.color,
                  flexShrink: 0,
                }}
              >
                {actionConfig.icon}
              </Box>
              {!isLast && (
                <Box
                  sx={{
                    width: 2,
                    flex: 1,
                    minHeight: 40,
                    bgcolor: colors.border,
                  }}
                />
              )}
            </Box>

            {/* Content */}
            <Box pb={isLast ? 0 : 3} flex={1} minWidth={0}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  fontFamily: typography.fontFamily.body,
                  color: colors.textPrimary,
                }}
              >
                {actionConfig.label}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: colors.textSecondary }}
              >
                {formatDate(entry.createdAt)}
              </Typography>
              {entry.performedBy && (
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ color: colors.textSecondary }}
                >
                  par {entry.performedBy.firstName} {entry.performedBy.lastName}
                </Typography>
              )}
              {entry.metadata && Object.keys(entry.metadata).length > 0 && (
                <Box mt={0.5}>
                  <>
                    {entry.metadata.previousStatus && entry.metadata.newStatus && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ color: colors.textMuted }}
                      >
                        {String(entry.metadata.previousStatus)} → {String(entry.metadata.newStatus)}
                      </Typography>
                    )}
                    {entry.metadata.itemCount && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ color: colors.textMuted }}
                      >
                        {`${entry.metadata.itemCount} article(s)`}
                      </Typography>
                    )}
                    {entry.metadata.totalAmount && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ color: colors.textMuted }}
                      >
                        Montant: {new Intl.NumberFormat('fr-DZ', {
                          style: 'currency',
                          currency: 'DZD',
                        }).format(Number(entry.metadata.totalAmount))}
                      </Typography>
                    )}
                    {entry.metadata.sourceOrderNumber && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ color: colors.textMuted }}
                      >
                        Dupliqué depuis: {String(entry.metadata.sourceOrderNumber)}
                      </Typography>
                    )}
                  </>
                </Box>
              )}
              {entry.notes && (
                <Typography
                  variant="caption"
                  display="block"
                  sx={{ color: colors.textMuted, mt: 0.5 }}
                >
                  {entry.notes}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

export default RestockHistoryTimeline;
